import { useState } from 'react'
import { Loader2, CheckCircle, XCircle, ChevronUp, X, Bot, Play, Minimize2 } from 'lucide-react'
import { useBackgroundTaskStore, type BackgroundTask, type TaskStatus } from '../stores/backgroundTaskStore'

export function BackgroundTaskIndicator() {
  const { tasks, minimizedTaskIds, restoreTask, removeTask } = useBackgroundTaskStore()
  const [isExpanded, setIsExpanded] = useState(false)
  
  const minimizedTasks = [...tasks.values()].filter(t => minimizedTaskIds.has(t.id))
  const runningCount = minimizedTasks.filter(t => t.status === 'running').length
  
  if (minimizedTasks.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-40">
      {/* Collapsed indicator */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-2 px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl hover:bg-[#222] transition-colors"
        >
          {runningCount > 0 ? (
            <Loader2 size={16} className="text-orange-400 animate-spin" />
          ) : (
            <CheckCircle size={16} className="text-emerald-400" />
          )}
          <span className="text-sm text-white">
            {runningCount > 0 
              ? `${runningCount} task${runningCount > 1 ? 's' : ''} running`
              : `${minimizedTasks.length} task${minimizedTasks.length > 1 ? 's' : ''}`
            }
          </span>
          <ChevronUp size={14} className="text-white/50" />
        </button>
      )}

      {/* Expanded panel */}
      {isExpanded && (
        <div className="w-80 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <span className="text-sm font-medium text-white">Background Tasks</span>
            <button
              onClick={() => setIsExpanded(false)}
              className="p-1 hover:bg-white/10 rounded transition-colors"
            >
              <Minimize2 size={14} className="text-white/50" />
            </button>
          </div>

          {/* Task list */}
          <div className="max-h-64 overflow-y-auto">
            {minimizedTasks.map(task => (
              <TaskItem
                key={task.id}
                task={task}
                onRestore={() => {
                  restoreTask(task.id)
                  setIsExpanded(false)
                }}
                onRemove={() => removeTask(task.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface TaskItemProps {
  task: BackgroundTask
  onRestore: () => void
  onRemove: () => void
}

function TaskItem({ task, onRestore, onRemove }: TaskItemProps) {
  const isRunning = task.status === 'running'
  
  return (
    <div className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition-colors group">
      <TaskIcon type={task.type} status={task.status} />
      
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onRestore}>
        <div className="text-sm text-white truncate">{task.title}</div>
        {task.subtitle && (
          <div className="text-xs text-white/50 truncate">{task.subtitle}</div>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={onRestore}
          className="p-1 hover:bg-white/10 rounded transition-colors opacity-0 group-hover:opacity-100"
          title="Open"
        >
          <ChevronUp size={14} className="text-white/50 rotate-90" />
        </button>
        {!isRunning && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            className="p-1 hover:bg-white/10 rounded transition-colors opacity-0 group-hover:opacity-100"
            title="Dismiss"
          >
            <X size={14} className="text-white/50" />
          </button>
        )}
      </div>
    </div>
  )
}

function TaskIcon({ type, status }: { type: BackgroundTask['type']; status: TaskStatus }) {
  const Icon = type === 'scan' ? Bot : Play
  
  if (status === 'running') {
    return (
      <div className="relative">
        <Icon size={16} className="text-orange-400" />
        <Loader2 size={10} className="absolute -bottom-1 -right-1 text-orange-400 animate-spin" />
      </div>
    )
  }
  
  if (status === 'success') {
    return (
      <div className="relative">
        <Icon size={16} className="text-emerald-400" />
        <CheckCircle size={10} className="absolute -bottom-1 -right-1 text-emerald-400" />
      </div>
    )
  }
  
  return (
    <div className="relative">
      <Icon size={16} className="text-red-400" />
      <XCircle size={10} className="absolute -bottom-1 -right-1 text-red-400" />
    </div>
  )
}
